import { useEffect, useMemo, useRef, useState } from 'react';
import { useMap } from 'react-leaflet';
import * as shapefile from 'shapefile';
import L from 'leaflet';

const getOutCode = ([lon, lat], bounds) => {
  let code = 0;
  if (lon < bounds.west) code |= 1;
  if (lon > bounds.east) code |= 2;
  if (lat < bounds.south) code |= 4;
  if (lat > bounds.north) code |= 8;
  return code;
};

const clipSegmentToBounds = (start, end, bounds) => {
  let x0 = start[0];
  let y0 = start[1];
  let x1 = end[0];
  let y1 = end[1];
  let out0 = getOutCode([x0, y0], bounds);
  let out1 = getOutCode([x1, y1], bounds);

  while (true) {
    if (!(out0 | out1)) return [[x0, y0], [x1, y1]];
    if (out0 & out1) return null;

    const out = out0 || out1;
    let x;
    let y;

    if (out & 8) {
      x = x0 + ((x1 - x0) * (bounds.north - y0)) / (y1 - y0);
      y = bounds.north;
    } else if (out & 4) {
      x = x0 + ((x1 - x0) * (bounds.south - y0)) / (y1 - y0);
      y = bounds.south;
    } else if (out & 2) {
      y = y0 + ((y1 - y0) * (bounds.east - x0)) / (x1 - x0);
      x = bounds.east;
    } else {
      y = y0 + ((y1 - y0) * (bounds.west - x0)) / (x1 - x0);
      x = bounds.west;
    }

    if (out === out0) {
      x0 = x;
      y0 = y;
      out0 = getOutCode([x0, y0], bounds);
    } else {
      x1 = x;
      y1 = y;
      out1 = getOutCode([x1, y1], bounds);
    }
  }
};

const sameCoordinate = (a, b) => (
  Math.abs(a[0] - b[0]) < 0.000001 &&
  Math.abs(a[1] - b[1]) < 0.000001
);

const clipPathToBounds = (coordinates, bounds) => {
  const lines = [];
  let currentLine = [];

  for (let index = 0; index < coordinates.length - 1; index += 1) {
    const clipped = clipSegmentToBounds(coordinates[index], coordinates[index + 1], bounds);
    if (!clipped) {
      if (currentLine.length > 1) lines.push(currentLine);
      currentLine = [];
      continue;
    }

    const [start, end] = clipped;
    if (currentLine.length === 0) {
      currentLine = [start, end];
    } else if (sameCoordinate(currentLine[currentLine.length - 1], start)) {
      currentLine.push(end);
    } else {
      if (currentLine.length > 1) lines.push(currentLine);
      currentLine = [start, end];
    }
  }

  if (currentLine.length > 1) lines.push(currentLine);
  return lines;
};

const clipGeometryToBounds = (geometry, bounds) => {
  if (!geometry || !bounds) return [geometry].filter(Boolean);

  const lineStrings = [];
  const addClippedPaths = (paths) => {
    paths.forEach(path => {
      lineStrings.push(...clipPathToBounds(path, bounds));
    });
  };

  if (geometry.type === 'LineString') {
    addClippedPaths([geometry.coordinates]);
  } else if (geometry.type === 'MultiLineString') {
    addClippedPaths(geometry.coordinates);
  } else if (geometry.type === 'Polygon') {
    addClippedPaths(geometry.coordinates);
  } else if (geometry.type === 'MultiPolygon') {
    geometry.coordinates.forEach(polygon => addClippedPaths(polygon));
  }

  return lineStrings.map(coordinates => ({
    type: 'LineString',
    coordinates
  }));
};

const clipGeoJsonToBounds = (geojson, bounds) => {
  if (!bounds || !geojson?.features) return geojson;
  return {
    ...geojson,
    features: geojson.features.flatMap(feature => (
      clipGeometryToBounds(feature.geometry, bounds).map(geometry => ({
        ...feature,
        geometry
      }))
    ))
  };
};

const shapefileCache = new Map();

const fetchArrayBuffer = async (path, label) => {
  const response = await fetch(path);
  if (!response.ok) {
    console.error(`SHAPEFILE ERROR: Could not load ${label}: ${path}, Status: ${response.status}`);
    throw new Error(`Failed to fetch shapefile (${label}): ${response.status}`);
  }
  return response.arrayBuffer();
};

const readShapefileWithoutDbf = async (shpPath) => {
  const shpBuffer = await fetchArrayBuffer(shpPath, '.shp');

  const source = await shapefile.openShp(shpBuffer);
  const geojson = {
    type: "FeatureCollection",
    features: []
  };

  let result = await source.read();
  while (!result.done) {
    geojson.features.push({
      type: "Feature",
      properties: {},
      geometry: result.value
    });
    result = await source.read();
  }

  return geojson;
};

const readShapefileWithDbf = async (shpPath, dbfPath) => {
  const [shpBuffer, dbfBuffer] = await Promise.all([
    fetchArrayBuffer(shpPath, '.shp'),
    fetchArrayBuffer(dbfPath, '.dbf')
  ]);
  return shapefile.read(shpBuffer, dbfBuffer);
};

const getCachedGeoJson = (cacheKey, loadGeoJson) => {
  if (!shapefileCache.has(cacheKey)) {
    shapefileCache.set(cacheKey, loadGeoJson());
  }
  return shapefileCache.get(cacheKey);
};

function ShapefileLayer({ url, borderColor, fillColor, opacity, fillOpacity, weight, clipBounds, visible = true, preload = false }) {
  const map = useMap();
  const layerRef = useRef(null);
  const loadedKeyRef = useRef('');
  const loadTokenRef = useRef(0);
  const [layerVersion, setLayerVersion] = useState(0);

  // Default colors if not provided
  const defaultBorderColor = '#E8853D'; // Darker orange for the border
  const defaultFillColor = '#FFCCA2';   // Soft pale orange fill
  const defaultOpacity = 0.4;           // Default border opacity
  const defaultFillOpacity = 0.15;      // Default fill opacity
  const defaultWeight = 1.5;            // Default stroke weight

  // Use provided colors/opacities or defaults
  const actualBorderColor = borderColor || defaultBorderColor;
  const actualFillColor = fillColor || defaultFillColor;
  const actualOpacity = opacity !== undefined ? opacity : defaultOpacity;
  const actualFillOpacity = fillOpacity !== undefined ? fillOpacity : defaultFillOpacity;
  const actualWeight = weight !== undefined ? weight : defaultWeight;

  // Create a unique pane ID based on the shapefile URL
  const getPaneId = () => {
    if (!url) return 'shapefile';

    // Extract filename from the URL and remove extension
    const filename = url.split('/').pop().replace('.shp', '');
    return `shapefile-${filename}`;
  };

  // Calculate paneId outside of useEffect since it only depends on url
  const paneId = getPaneId();
  const shpPath = url || `${process.env.PUBLIC_URL}/spatial/Trawl_WGS84.shp`;
  const clipBoundsKey = clipBounds ? JSON.stringify(clipBounds) : '';
  const layerKey = useMemo(() => ([
    shpPath,
    paneId,
    actualBorderColor,
    actualFillColor,
    actualOpacity,
    actualFillOpacity,
    actualWeight,
    clipBoundsKey
  ].join('|')), [
    shpPath,
    paneId,
    actualBorderColor,
    actualFillColor,
    actualOpacity,
    actualFillOpacity,
    actualWeight,
    clipBoundsKey
  ]);

  useEffect(() => {
    let cancelled = false;
    let idleHandle = null;
    let timeoutHandle = null;
    const token = loadTokenRef.current + 1;
    loadTokenRef.current = token;

    const shouldLoad = visible || preload;
    if (!shouldLoad) return undefined;

    if (layerRef.current && loadedKeyRef.current === layerKey) return undefined;

    const loadShapefile = async () => {
      try {
        if (layerRef.current) {
          map.removeLayer(layerRef.current);
          layerRef.current = null;
          loadedKeyRef.current = '';
        }

        // Create a custom pane for this shapefile layer that sits below the marker pane
        if (!map.getPane(paneId)) {
          map.createPane(paneId);
          map.getPane(paneId).style.zIndex = 350; // Default marker pane is 400
          map.getPane(paneId).style.pointerEvents = 'none'; // Make the pane not intercept pointer events
        }

        let geoJsonLayer = null;

        // Special handling for BLL_WGS84_2015 file which may have issues with dbf file
        if (url && url.includes('BLL_WGS84_2015')) {
          // Create a geojson directly from shapefile geometry without attributes
          try {
            const geojson = await getCachedGeoJson(`shp-only:${shpPath}`, () => readShapefileWithoutDbf(shpPath));

            const renderedGeojson = clipGeoJsonToBounds(geojson, clipBounds);

            // Create the GeoJSON layer with styling
            geoJsonLayer = L.geoJSON(renderedGeojson, {
              pane: paneId,
              style: {
                color: actualBorderColor,
                weight: actualWeight,
                opacity: actualOpacity,
                fillColor: actualFillColor,
                fillOpacity: actualFillOpacity,
                interactive: false
              }
            });

            if (cancelled || loadTokenRef.current !== token) {
              map.removeLayer(geoJsonLayer);
              return;
            }

            layerRef.current = geoJsonLayer;
            loadedKeyRef.current = layerKey;
            setLayerVersion(version => version + 1);

            if (visible) {
              geoJsonLayer.addTo(map);
            }
          } catch (err) {
            console.error(`Failed to convert BLL_WGS84_2015 shapefile:`, err);

            // Fallback to a predefined polygon if all else fails
            console.warn(`Using fallback polygon for BLL_WGS84_2015`);
            const fallbackGeoJson = {
              type: "FeatureCollection",
              features: [{
                type: "Feature",
                properties: {},
                geometry: {
                  type: "Polygon",
                  coordinates: [
                    [
                      [-85, 28],
                      [-85, 29],
                      [-86, 29],
                      [-86, 28],
                      [-85, 28]
                    ]
                  ]
                }
              }]
            };

            geoJsonLayer = L.geoJSON(fallbackGeoJson, {
              pane: paneId,
              style: {
                color: actualBorderColor,
                weight: actualWeight,
                opacity: actualOpacity,
                fillColor: actualFillColor,
                fillOpacity: actualFillOpacity,
                dashArray: "5, 5",
                interactive: false
              }
            });

            if (cancelled || loadTokenRef.current !== token) {
              map.removeLayer(geoJsonLayer);
              return;
            }

            layerRef.current = geoJsonLayer;
            loadedKeyRef.current = layerKey;
            setLayerVersion(version => version + 1);

            if (visible) {
              geoJsonLayer.addTo(map);
            }
          }
        } else {
          // Standard handling for other shapefiles
          // Generate corresponding dbf path by replacing .shp with .dbf
          const dbfPath = shpPath.replace('.shp', '.dbf');

          // Convert to GeoJSON
          try {
            const geojson = await getCachedGeoJson(`shp-dbf:${shpPath}:${dbfPath}`, () => readShapefileWithDbf(shpPath, dbfPath));

            // Handle potential empty features
            if (!geojson.features || geojson.features.length === 0) {
              console.warn(`WARNING: Shapefile contains no features.`);
              return;
            }

            const renderedGeojson = clipGeoJsonToBounds(geojson, clipBounds);

            // Create GeoJSON layer with styling - using specified colors
            geoJsonLayer = L.geoJSON(renderedGeojson, {
              pane: paneId, // Assign to our custom pane
              style: {
                color: actualBorderColor,
                weight: actualWeight,
                opacity: actualOpacity,
                fillColor: actualFillColor,
                fillOpacity: actualFillOpacity,
                interactive: false
              }
            });

            if (!renderedGeojson || !renderedGeojson.features) {
              console.warn(`GeoJSON is missing features array or is malformed`);
            }

            if (cancelled || loadTokenRef.current !== token) {
              map.removeLayer(geoJsonLayer);
              return;
            }

            layerRef.current = geoJsonLayer;
            loadedKeyRef.current = layerKey;
            setLayerVersion(version => version + 1);

            if (visible) {
              geoJsonLayer.addTo(map);
            }
          } catch (conversionError) {
            console.error(`SHAPEFILE ERROR: Failed to convert shapefile to GeoJSON:`, conversionError);
            throw conversionError;
          }
        }
      } catch (error) {
        console.error(`Error loading shapefile ${url}:`, error);
      }
    };

    if (preload && !visible) {
      if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        idleHandle = window.requestIdleCallback(loadShapefile, { timeout: 1500 });
      } else {
        timeoutHandle = window.setTimeout(loadShapefile, 250);
      }
    } else {
      loadShapefile();
    }

    return () => {
      cancelled = true;
      if (idleHandle && typeof window !== 'undefined' && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleHandle);
      }
      if (timeoutHandle) window.clearTimeout(timeoutHandle);
    };
  }, [map, url, paneId, shpPath, layerKey, actualBorderColor, actualFillColor, actualOpacity, actualFillOpacity, actualWeight, clipBounds, visible, preload]);

  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;

    if (visible) {
      if (!map.hasLayer(layer)) layer.addTo(map);
    } else if (map.hasLayer(layer)) {
      map.removeLayer(layer);
    }
  }, [map, visible, layerVersion]);

  useEffect(() => () => {
    const layer = layerRef.current;
    if (layer && map.hasLayer(layer)) {
      map.removeLayer(layer);
    }
    layerRef.current = null;
    loadedKeyRef.current = '';
  }, [map, paneId]);

  return null; // This component doesn't render anything visible
}

export default ShapefileLayer;
